import { useState, useEffect } from 'react';
import { axiosInstance } from 'utils/axios';

// material-ui
import { Box, Button, Grid, Typography, TextField, Select, MenuItem, Autocomplete, CircularProgress, Checkbox } from '@mui/material';

import { gridSpacing } from 'app/constant';
import { deepPurple } from '@mui/material/colors';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import StickyHeadTable from './components/StickyHeadTable';
import { warningAlert, successAlert, errorAlert } from 'ui-component/Toasts';
import useFetchData from 'hooks/useFetchData';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const AmbassadorClaims = () => {
    const token = useSelector(selectCurrentToken);

    const [loginUserName, setLoginUserName] = useState('');
    const { data: loginUserNames } = useFetchData('/Customer/LoginUserNames');

    const [ambassadorClaims, setAmbassadorClaims] = useState([]);
    const [ambassadorClaimsLoading, setAmbassadorClaimsLoading] = useState(false);
    const [filterAmbassadorClaimsFrom, setFilterAmbassadorClaimsFrom] = useState(dayjs().subtract(1, 'days'));
    const [filterAmbassadorClaimsTo, setFilterAmbassadorClaimsTo] = useState(dayjs());
    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);

    const fetchAmbassadorClaims = async () => {
        if (!filterAmbassadorClaimsFrom.isValid() || !filterAmbassadorClaimsTo.isValid()) {
            warningAlert('Please fill the required fields.');
            return;
        }
        let ambassadorClaimsUrl = `/AmbassadorClaims/All?dateFrom=${filterAmbassadorClaimsFrom.format('YYYY-MM-DD')}&dateTo=${filterAmbassadorClaimsTo.format('YYYY-MM-DD')}`;
        if (loginUserName !== '') {
            ambassadorClaimsUrl += `&loginUserName=${loginUserName}`;
        }

        setAmbassadorClaimsLoading(true);
        try {
            const res = await axiosInstance.get(ambassadorClaimsUrl, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setAmbassadorClaims(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setAmbassadorClaimsLoading(false);
        }
    };
    
        const [exportDataListLoading, setExportDataListLoading] = useState(false);
        function convertDataForSheet(data) {
            if (!data || data.length === 0) {
                warningAlert('There is no data to export');
                throw new Error('Data array is empty or undefined.');
            }
    
            const headers = Object.keys(data[0]);
            const dataArray = [headers];
    
            data.forEach((item) => {
                const row = headers.map((key) => item[key]);
                dataArray.push(row);
            });
    
            return XLSX.utils.aoa_to_sheet(dataArray);
        }

        const exportDataAsXlsx = async () => {
            setFilterBtnDisabled(true);
            setTimeout(() => {
                setFilterBtnDisabled(false);
            }, 3000);
            setExportDataListLoading(true);
            if (!filterAmbassadorClaimsFrom.isValid() || !filterAmbassadorClaimsTo.isValid()) {
                warningAlert('Please fill up all required fields.');
                return;
            }

            let ambassadorClaimsUrl = `/AmbassadorClaims/All?dateFrom=${filterAmbassadorClaimsFrom.format('YYYY-MM-DD')}&dateTo=${filterAmbassadorClaimsTo.format('YYYY-MM-DD')}`;
            if (loginUserName !== '') {
                ambassadorClaimsUrl += `&loginUserName=${loginUserName}`;
            }
            
            try {
                const res = await axiosInstance.get(ambassadorClaimsUrl, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
    
                if (res.status === 200) {
                    const fileName = `Ambassador_Claims_${dayjs().format('MM-DD-YYYY')}`;
                    const workbook = XLSX.utils.book_new();
                    const worksheet = convertDataForSheet(res.data);
                    // Adjust column widths
                    const headers = Object.keys(res.data[0]);
                    const colWidths = headers.map((header) => ({ wch: header.length + 2 }));
    
                    worksheet['!cols'] = colWidths;
    
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
                    XLSX.writeFile(workbook, `${fileName}.xlsx`);
                } else {
                    errorAlert('Something went wrong.');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setExportDataListLoading(false);
            }
        };

    useEffect(() => {
        fetchAmbassadorClaims();
    }, []);

    return (
        <Grid container item xs={12} padding="20px">
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>Label From :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={filterAmbassadorClaimsFrom}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setFilterAmbassadorClaimsFrom(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>Label To :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={filterAmbassadorClaimsTo}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setFilterAmbassadorClaimsTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Login UserName :
                        </Typography>

                        <Autocomplete
                            freeSolo
                            value={loginUserName}
                            onChange={(event, newValue) => {
                                setLoginUserName(newValue);
                            }}
                            inputValue={loginUserName}
                            onInputChange={(event, newInputValue) => {
                                setLoginUserName(newInputValue);
                            }}
                            id="controllable-states-demo"
                            options={loginUserNames}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={12} lg={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            gap: '15px',
                            width: '50%',
                            marginLeft: 'auto'
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{
                                float: 'right',
                                display: 'block',
                                mx: 'auto',
                                width: '150px',
                                backgroundColor: deepPurple[500],
                                marginTop: '20px',
                                marginBottom: '30px'
                            }}
                            onClick={() => fetchAmbassadorClaims()}
                            disabled={ambassadorClaimsLoading}
                        >
                            Filter
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                display: 'block',
                                mx: 'auto',
                                width: '150px',
                                backgroundColor: deepPurple[500],
                                marginTop: '20px',
                                marginBottom: '20px'
                            }}
                            onClick={() => exportDataAsXlsx()}
                            disabled={filterBtnDisabled}
                        >
                            Export Data
                        </Button>
                    </Box>
                </Grid>

                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {ambassadorClaimsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable ambassadorClaims={ambassadorClaims} />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default AmbassadorClaims;
